import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import DetailsRefusalModal from 'components/DetailsRefusalModal/DetailsRefusalModal';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import Toggle from 'components/Toggle/Toggle';
import Tooltip from 'components/Tooltip/Tooltip';
import { MODAL_REFINEMENT_QUESTIONS, TOPIC_REFINEMENT_NAME, ZERO_VALUE } from 'constants/general';
import { BUTTON_VARIANT, TOOLTIP_POSITIONS } from 'constants/interfaces';

import RefinementDropdownList from './RefinementDropdownList';

import type ITopicRefinementItem from 'store/slices/materialAssessment/interfaces/ITopicRefinementItem';
import type ISelectMediaTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  topicRefinementII,
  handleToggle,
  handleToItem,
  IROIdentification,
  handleOpenModalRefinementQuestions,
  currentGroupType,
  currentType,
  currentGroupId,
  handleCloseModalRefinementQuestions,
  disableToggle,
}: ISelectMediaTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ITopicRefinementItem | null>(null);
  const [isCompaniesDropdownOpen, setIsCompaniesDropdownOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);
  const companiesDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (companiesDropdownRef.current && target && !companiesDropdownRef.current.contains(target)) {
        setIsCompaniesDropdownOpen(false);
        handleCloseModalRefinementQuestions();
      }
    };

    if (isCompaniesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCompaniesDropdownOpen, handleCloseModalRefinementQuestions]);

  useEffect(() => {
    if ((currentType === MODAL_REFINEMENT_QUESTIONS.COMPANIES
      || currentType === MODAL_REFINEMENT_QUESTIONS.REGIONS)
      && currentGroupId) {
      setIsCompaniesDropdownOpen(true);
      return;
    }
    setIsCompaniesDropdownOpen(false);
  }, [currentType, currentGroupId]);

  const calculatePercentage = (mentioned: string | number, total: string | number): string => {
    const mentionedNum = Number(mentioned);
    const totalNum = Number(total);
    if (!mentionedNum || !totalNum || totalNum === ZERO_VALUE) {
      return `${ZERO_VALUE}`;
    }
    return ((mentionedNum / totalNum) * 100).toFixed(0);
  };

  const handleOpenInfoModal = (item: ITopicRefinementItem) => {
    setSelectedItem(item);
    setIsInfoModalVisible(true);
  };

  const handleCloseInfoModal = () => {
    setIsInfoModalVisible(false);
    setSelectedItem(null);
  };

  const handleCompaniesButtonClick = (itemId: number) => {
    handleOpenModalRefinementQuestions(itemId, MODAL_REFINEMENT_QUESTIONS.COMPANIES);
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="NGOs table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }, index) => {
              const isLastThreeColumns = index >= headCells.length - 4;
              if (isLastThreeColumns && !topicRefinementII && !IROIdentification) {
                return null;
              }
              const isLastThreeColumns2 = index >= headCells.length - 1;
              if (isLastThreeColumns2 && !IROIdentification && topicRefinementII) {
                return null;
              }
              return (
                <TableCell head className={styles.tableCell} key={id}>
                  <div className={styles.headerLabelWrapper}>
                    <span className={styles.tableCellLabel}>
                      {t(label)}
                    </span>
                    {id === 'totalCompaniesCount' ? (
                      <Tooltip
                        hint={t('materialityAssessment.companiesCountTooltip')}
                        hintClassName={clsx(styles.hint, styles.companiesCountHint)}
                        position={TOOLTIP_POSITIONS.BOTTOM}
                      >
                        <IconButton
                          icon={<InfoIcon />}
                          className={styles.headerInfoIcon}
                          aria-label="Companies count information"
                          onClick={() => {}}
                        />
                      </Tooltip>
                    ) : null}
                    {id === 'totalRegionCount' ? (
                      <Tooltip
                        hint={t('materialityAssessment.regionCountTooltip')}
                        hintClassName={clsx(styles.hint, styles.regionCountHint)}
                        position={TOOLTIP_POSITIONS.BOTTOM}
                      >
                        <IconButton
                          icon={<InfoIcon />}
                          className={styles.headerInfoIcon}
                          aria-label="Region count information"
                          onClick={() => {}}
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                </TableCell>
              );
            })}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>{item.topicName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span>{item.subTopicName || hyphen}</span>
                </TableCell>
                <TableCell>
                  <span className={styles.nameCell}>{item?.valueChainMap?.name || '-'}</span>
                </TableCell>
                <TableCell>
                  {item.standardName || hyphen}
                </TableCell>
                <TableCell>
                  <div className={styles.toggleWrapper}>
                    <Toggle
                      value={`${TOPIC_REFINEMENT_NAME}_${item.id}`}
                      checked={item.isActive}
                      name={`${TOPIC_REFINEMENT_NAME}_${item.id}`}
                      onChange={() => handleToggle(item.id)}
                      classNameIndicator={styles.toggleBackground}
                      disabled={disableToggle}
                    />
                    {item.isActive ? (
                      <span className={clsx(styles.rightText, {
                        [styles.activeRight]: item.isActive,
                      })}
                      >
                        {t('materialityAssessment.included')}
                      </span>
                    ) : (
                      <span className={clsx(styles.leftText, {
                        [styles.activeLeft]: !item.isActive,
                      })}
                      >
                        {t('materialityAssessment.excluded')}
                      </span>
                    )}
                    {item.maTopicAnalysisLatestLog ? (
                      <IconButton
                        icon={<InfoIcon />}
                        className={styles.infoButton}
                        onClick={() => handleOpenInfoModal(item)}
                      />
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  {item.questionCount ? (
                    <Button
                      className={styles.buttonIndicate}
                      title={`${item.questionCount}`}
                      variant={BUTTON_VARIANT.ghost}
                      onClick={() => handleOpenModalRefinementQuestions(item.id, MODAL_REFINEMENT_QUESTIONS.QUESTIONS)}
                    />
                  ) : hyphen}
                </TableCell>
                <TableCell>
                  {item.totalCompaniesCount ? (
                    <div
                      className={styles.companiesDropdownWrapper}
                      ref={
                        currentType === MODAL_REFINEMENT_QUESTIONS.COMPANIES
                        && item.id === currentGroupId
                          ? companiesDropdownRef
                          : undefined
                      }
                    >
                      <Button
                        className={styles.buttonIndicate}
                        title={`${item?.totalCompaniesCount && item?.mentionedInCompaniesCount
                          ? `${item?.mentionedInCompaniesCount}/${item?.totalCompaniesCount}
                      (${calculatePercentage(item?.mentionedInCompaniesCount, item?.totalCompaniesCount)})%` : hyphen}`}
                        variant={BUTTON_VARIANT.ghost}
                        onClick={() => handleCompaniesButtonClick(item.id)}
                        aria-haspopup="listbox"
                        aria-expanded={
                          currentType === MODAL_REFINEMENT_QUESTIONS.COMPANIES
                          && item.id === currentGroupId
                          && isCompaniesDropdownOpen
                        }
                      />
                      {currentType === MODAL_REFINEMENT_QUESTIONS.COMPANIES
                      && item.id === currentGroupId
                      && isCompaniesDropdownOpen ? (
                        <RefinementDropdownList
                          items={currentGroupType}
                          ariaLabel="Companies refinement group"
                        />
                        ) : null}
                    </div>
                  ) : hyphen}
                </TableCell>
                <TableCell>
                  {item.disclosureCount ? (
                    <Button
                      className={styles.buttonIndicate}
                      title={`${item.disclosureCount}`}
                      variant={BUTTON_VARIANT.ghost}
                      onClick={() => handleOpenModalRefinementQuestions(
                        item.id,
                        MODAL_REFINEMENT_QUESTIONS.DISCLOSURES,
                      )}
                    />
                  ) : hyphen}
                </TableCell>
                <TableCell>
                  {item.totalRegionsCount ? (
                    <div
                      className={styles.companiesDropdownWrapper}
                      ref={
                        currentType === MODAL_REFINEMENT_QUESTIONS.REGIONS
                        && item.id === currentGroupId
                          ? companiesDropdownRef
                          : undefined
                      }
                    >
                      <Button
                        className={styles.buttonIndicate}
                        title={`${item?.totalRegionsCount && item?.mentionedInRegionsCount
                          ? `${item?.mentionedInRegionsCount}/${item?.totalRegionsCount}
                      (${calculatePercentage(item?.mentionedInRegionsCount, item?.totalRegionsCount)})%` : hyphen}`}
                        variant={BUTTON_VARIANT.ghost}
                        onClick={() => handleOpenModalRefinementQuestions(item.id, MODAL_REFINEMENT_QUESTIONS.REGIONS)}
                        aria-haspopup="listbox"
                        aria-expanded={
                          currentType === MODAL_REFINEMENT_QUESTIONS.REGIONS
                          && item.id === currentGroupId
                          && isCompaniesDropdownOpen
                        }
                      />
                      {currentType === MODAL_REFINEMENT_QUESTIONS.REGIONS
                      && item.id === currentGroupId
                      && isCompaniesDropdownOpen ? (
                        <RefinementDropdownList
                          items={currentGroupType}
                          ariaLabel="Regions refinement group"
                        />
                        ) : null}
                    </div>
                  ) : hyphen}
                </TableCell>
                {topicRefinementII || IROIdentification ? (
                  <>
                    <TableCell>
                      {item.surveyPosition?.toFixed(2) || hyphen}
                    </TableCell>
                    <TableCell>
                      {item.interviewsPosition?.toFixed(2) || hyphen}
                    </TableCell>
                    <TableCell>
                      {item.roundTablesPosition?.toFixed(2) || hyphen}
                    </TableCell>
                  </>
                ) : null}
                { IROIdentification ? (
                  <TableCell>
                    <Tooltip
                      hint={t('materialityAssessment.viewRespondentSurveys')}
                      hintClassName={styles.hint}
                      position={index === 0 ? TOOLTIP_POSITIONS.BOTTOM : undefined}
                    >
                      <IconButton
                        icon={<EyeIcon />}
                        onClick={() => handleToItem?.(item.topicId || -1)}
                      />
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
      <DetailsRefusalModal
        open={isInfoModalVisible}
        title={t(
          'materialityAssessment.detailsInclusionExclusion',
          { type: selectedItem?.isActive ? 'Included' : 'Excluded' },
        )}
        onClose={handleCloseInfoModal}
        person={`${selectedItem?.maTopicAnalysisLatestLog?.initiatorFirstName} 
        ${selectedItem?.maTopicAnalysisLatestLog?.initiatorLastName}`}
        date={selectedItem?.maTopicAnalysisLatestLog?.createdAt}
        comment={selectedItem?.maTopicAnalysisLatestLog?.reason || ''}
      />
    </div>
  );
}

export default ItemsTable;
